/**
 * ZoomPanContext - Shared zoom/pan state for synchronized multi-image viewing
 *
 * Provides React Context for coordinating zoom and pan operations across multiple
 * DetailViewItem2 instances within a DetailView2 container.
 *
 * Features:
 * - Synchronized zoom level (fit | 200) across all images
 * - Percentage-based pan synchronization (handles different aspect ratios)
 * - Loading coordination (wait for all images before applying zoom/pan)
 * - Dynamic image addition/removal (new images adopt current state)
 *
 * @module ZoomPanContext
 */

import { createContext } from 'react';
import type { ZoomLevel } from '@/hooks/useZoomPan';

/**
 * Shared zoom/pan state managed at DetailView2 level
 * Broadcast via React Context to all DetailViewItem2 instances
 */
export interface SharedZoomPanState {
  /** Current zoom level (applies to ALL images) */
  zoomLevel: ZoomLevel;

  /** Horizontal pan as percentage 0-100 */
  panXPercent: number;

  /** Vertical pan as percentage 0-100 */
  panYPercent: number;

  /** Number of images currently loading variants */
  loadingCount: number;

  /** Is any image being actively dragged? */
  isPanning: boolean;
}

/**
 * Callbacks for updating shared state
 * Provided by DetailView2, consumed by DetailViewItem2 instances
 */
export interface SharedZoomPanCallbacks {
  /**
   * Called when any image initiates a zoom change
   *
   * @param level - New zoom level
   * @param cursorPercentX - Click position as % of image width (optional)
   * @param cursorPercentY - Click position as % of image height (optional)
   */
  onZoomChange: (
    level: ZoomLevel,
    cursorPercentX?: number,
    cursorPercentY?: number
  ) => void;

  /**
   * Called when any image is being panned
   *
   * @param panXPercent - New horizontal pan position as percentage
   * @param panYPercent - New vertical pan position as percentage
   */
  onPanChange: (
    panXPercent: number,
    panYPercent: number
  ) => void;

  /**
   * Called when an image starts or finishes loading a variant
   *
   * @param isLoading - true = start loading, false = finished loading
   */
  onLoadingChange: (
    isLoading: boolean
  ) => void;
}

/**
 * Complete context value combining state and callbacks
 * Provided by DetailView2 via ZoomPanContext.Provider
 */
export interface ZoomPanContextValue extends SharedZoomPanState, SharedZoomPanCallbacks {}

/**
 * React Context for sharing zoom/pan state across DetailViewItem2 instances
 *
 * - Created and provided by DetailView2
 * - Consumed by DetailViewItem2 via useContext()
 * - Optional: If context is null, DetailViewItem2 operates in standalone mode
 */
export const ZoomPanContext = createContext<ZoomPanContextValue | null>(null);

ZoomPanContext.displayName = 'ZoomPanContext';
